/**
 * Componente: UsersTab
 * Descrição: Aba de Usuários da tela de Gerenciamento do Tenant
 * 
 * Exibe:
 * - Cards de resumo (total, ativos, por perfil)
 * - Tabela de membros com avatar, nome, email, perfil, status
 * 
 * @author Sistema
 * @update 2026-01-19 - Criação inicial (Fase 3)
 */

import { 
  Users, 
  UserCheck, 
  UserX,
  Crown,
  Shield,
  User,
  Circle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { TenantMemberWithProfile } from "@/hooks/useTenantManagement";

// ============================================================================
// INTERFACES
// ============================================================================

interface UsersTabProps {
  members: TenantMemberWithProfile[];
  maxAgents: number;
  isEnterprise: boolean;
  isLoading?: boolean;
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

/**
 * Card de resumo de usuários
 */
const UserSummaryCard = ({
  title,
  value,
  icon: Icon,
  variant = "default",
  description,
  isLoading,
}: {
  title: string;
  value: number | string;
  icon: React.ElementType;
  variant?: "default" | "success" | "warning" | "muted";
  description?: string;
  isLoading?: boolean;
}) => {
  const variantStyles = {
    default: "bg-primary/10 text-primary",
    success: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    muted: "bg-muted text-muted-foreground",
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div>
              <Skeleton className="h-4 w-20 mb-1" />
              <Skeleton className="h-6 w-12" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className={cn(
            "flex h-10 w-10 items-center justify-center rounded-lg",
            variantStyles[variant]
          )}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-xl font-bold">{value}</p>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Indicador de status online
 */
const OnlineStatusIndicator = ({ status }: { status: string | null }) => {
  const statusConfig = {
    online: {
      color: "bg-emerald-500",
      label: "Online",
    },
    away: {
      color: "bg-amber-500",
      label: "Ausente",
    },
    offline: {
      color: "bg-muted-foreground/50",
      label: "Offline",
    },
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.offline;

  return (
    <div className="flex items-center gap-2">
      <Circle className={cn("h-2.5 w-2.5 fill-current", config.color.replace("bg-", "text-"))} />
      <span className="text-xs text-muted-foreground">{config.label}</span>
    </div>
  );
};

/**
 * Badge de perfil de permissão
 */
const RoleBadge = ({ role, isOwner }: { role: string | null; isOwner: boolean }) => {
  if (isOwner) {
    return (
      <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0 gap-1">
        <Crown className="h-3 w-3" />
        Proprietário
      </Badge>
    );
  }

  const roleStyles: Record<string, { icon: React.ElementType; className: string; label: string }> = {
    tenant_admin: {
      icon: Shield,
      className: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
      label: "Admin",
    },
    tenant_supervisor: {
      icon: Shield,
      className: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      label: "Supervisor",
    },
    tenant_agent: {
      icon: User,
      className: "bg-muted text-muted-foreground",
      label: "Agente",
    },
  };

  const config = roleStyles[role || ""] || {
    icon: User,
    className: "bg-muted text-muted-foreground",
    label: role || "Agente",
  };

  const Icon = config.icon;

  return (
    <Badge variant="secondary" className={cn("border-0 gap-1", config.className)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function UsersTab({ members, maxAgents, isEnterprise, isLoading }: UsersTabProps) {
  // Contagens
  const totalMembers = members.length;
  const onlineMembers = members.filter((m) => m.online_status === "online").length;
  const activeMembers = members.filter((m) => m.is_active).length;
  const owners = members.filter((m) => m.is_owner).length;

  // Agrupar por perfil
  const adminCount = members.filter((m) => m.permission_profile?.slug === "tenant_admin").length;
  const supervisorCount = members.filter((m) => m.permission_profile?.slug === "tenant_supervisor").length;
  const agentCount = members.filter((m) => 
    !m.permission_profile?.slug || 
    m.permission_profile?.slug === "tenant_agent"
  ).length;

  return (
    <div className="space-y-6">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <UserSummaryCard
          title="Total de Usuários"
          value={`${totalMembers} / ${maxAgents}`}
          icon={Users}
          variant="default"
          isLoading={isLoading}
        />
        <UserSummaryCard
          title="Online Agora"
          value={onlineMembers}
          icon={UserCheck}
          variant="success"
          isLoading={isLoading}
        />
        <UserSummaryCard
          title="Admins"
          value={adminCount + owners}
          icon={Shield}
          variant="warning"
          isLoading={isLoading}
        />
        <UserSummaryCard
          title="Agentes"
          value={agentCount}
          icon={User}
          variant="muted"
          isLoading={isLoading}
        />
      </div>

      {/* Tabela de Membros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Membros do Workspace</CardTitle>
          <CardDescription>
            Lista completa de usuários cadastrados
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : members.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum membro encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Usuário</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead>Última Atividade</TableHead>
                    <TableHead>Ingresso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      {/* Avatar + Nome + Email */}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <Avatar className="h-9 w-9">
                              <AvatarImage 
                                src={member.profile?.avatar_url || undefined} 
                                alt={member.profile?.full_name || ""} 
                              />
                              <AvatarFallback className="text-xs">
                                {(member.profile?.full_name || member.profile?.email || "U")
                                  .charAt(0)
                                  .toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            {/* Indicador online no avatar */}
                            {member.online_status === "online" && (
                              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {member.profile?.full_name || "Sem nome"}
                            </p>
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {member.profile?.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>

                      {/* Perfil de Permissão */}
                      <TableCell>
                        <RoleBadge 
                          role={member.permission_profile?.slug || null} 
                          isOwner={member.is_owner} 
                        />
                      </TableCell>

                      {/* Status Online */}
                      <TableCell className="text-center">
                        <OnlineStatusIndicator status={member.online_status} />
                      </TableCell>

                      {/* Última Atividade */}
                      <TableCell className="text-sm text-muted-foreground">
                        {member.last_seen_at ? (
                          formatDistanceToNow(new Date(member.last_seen_at), {
                            addSuffix: true,
                            locale: ptBR,
                          })
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      {/* Data de Ingresso */}
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(member.created_at), "dd/MM/yyyy", { locale: ptBR })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
